import React, { useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { asArray } from "@/lib/safeData";
import { Shield, AlertTriangle, Skull, Eye } from "lucide-react";

const verdictMeta = {
  "DANGER": { color: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10", Icon: Skull },
  "HIGH RISK": { color: "text-[#FF6A60] border-[#FF6A60]/40 bg-[#FF3B30]/5", Icon: AlertTriangle },
  "CAUTION": { color: "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/5", Icon: Eye },
  "NO MATCH": { color: "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/5", Icon: Shield },
};

const RiskChecker = () => {
  const { t, lang } = useT();
  const [form, setForm] = useState({ telegram_username: "", upi_id: "", phone: "", website: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!Object.values(form).some(Boolean)) {
      setError(lang === "hi" ? "कोई भी एक फ़ील्ड भरें — सभी वैकल्पिक हैं।" : "Fill any one of the four — all fields are optional.");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/risk-check", form);
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";
  const meta = result ? verdictMeta[result.verdict] || verdictMeta["NO MATCH"] : null;

  return (
    <div className="mx-auto max-w-4xl px-4 md:px-8 py-16 md:py-24" data-testid="risk-page">
      <SectionHeading eyebrow="Verify Before You Pay" title={t.risk.title} subtitle={t.risk.subtitle} />

      <div className="mt-6 rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] px-4 py-3 text-xs text-[#E5C46B] inline-flex items-center gap-2">
        <span className="font-mono-alt uppercase tracking-widest text-[10px]">Tip</span>
        <span>
          {lang === "hi"
            ? "सभी फ़ील्ड वैकल्पिक हैं — कोई भी एक (टेलीग्राम / UPI / फ़ोन / वेबसाइट) भरें।"
            : "All four fields are optional. Fill any one (Telegram, UPI, Phone, or Website) to run the check."}
        </span>
      </div>

      <form onSubmit={submit} className="mt-6 surface-card p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="risk-form">
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.risk.telegram}</Label>
          <Input value={form.telegram_username} onChange={set("telegram_username")} className={`mt-2 ${fieldCls}`} placeholder="@trader_xyz" data-testid="risk-tg-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.risk.upi}</Label>
          <Input value={form.upi_id} onChange={set("upi_id")} className={`mt-2 ${fieldCls}`} placeholder="trader@upi" data-testid="risk-upi-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.risk.phone}</Label>
          <Input value={form.phone} onChange={set("phone")} className={`mt-2 ${fieldCls}`} placeholder="+91 …" data-testid="risk-phone-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.risk.website}</Label>
          <Input value={form.website} onChange={set("website")} className={`mt-2 ${fieldCls}`} placeholder="trader.in" data-testid="risk-web-input" />
        </div>
        <div className="md:col-span-2">
          {error && <div className="mb-3 text-sm text-[#FF6A60]">{error}</div>}
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="risk-check-btn">
            {loading ? t.common.loading : t.risk.check}
          </Button>
        </div>
      </form>

      {result && meta && (
        <div className="mt-8 fade-up" data-testid="risk-result">
          <div className={`rounded-xl border ${meta.color} p-6 md:p-8`}>
            <div className="flex items-center gap-4">
              <meta.Icon className="h-10 w-10" />
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70 font-mono-alt">Verdict</div>
                <div className="font-display text-2xl md:text-3xl font-black tracking-tight">{t.risk.verdictLabels[result.verdict] || result.verdict}</div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${result.score}%`,
                    background: result.score >= 80 ? "#FF3B30" : result.score >= 60 ? "#FF6A60" : result.score >= 30 ? "#D4AF37" : "#10B981",
                  }}
                />
              </div>
              <div className="font-mono-alt text-2xl font-bold">{result.score}</div>
            </div>
            {asArray(result?.matches).length > 0 && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-widest font-mono-alt opacity-70">Matches</div>
                <div className="mt-2 space-y-2">
                  {asArray(result.matches).map((m, i) => (
                    <div key={m.id ?? `match-${i}`} className="rounded-md border border-white/10 bg-black/30 px-3 py-2 text-sm flex items-center justify-between">
                      <span>{m.handle}</span>
                      <span className="text-xs opacity-70">{m.status_tag} · matched {m.matched_on}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {result.verdict === "NO MATCH" && (
              <div className="mt-6 text-sm opacity-80 leading-relaxed">{t.risk.noMatchHelp}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskChecker;
